import { AxiosError, AxiosRequestConfig } from 'axios';
import { app } from 'store';
import { Store } from '@reduxjs/toolkit';
import { getClientTimezoneOffset } from './trackerMetadata/trackerMetadata';
import { StoreState } from 'types';

export function getBackendURL(path = '', version = 'v1') {
  const isAiDomain = window.location.hostname.includes('globalvision.ai');
  const envUrl = isAiDomain ? import.meta.env.VITE_VERIFY_CDN_API_URL : import.meta.env.VITE_VERIFY_API_URL;
  let backendUrl = '';
  if (envUrl && process.env.NODE_ENV !== 'development') {
    // this is ok with string undefined because of how vars are injected in prod
    backendUrl = `https://${envUrl.replace('verify-web', 'verify-api').replace(/[\r\n]/g, '')}`;
  }
  if (!path) {
    return `${backendUrl}/api/${version}/`;
  }
  return `${backendUrl}/api/${version}/${path}`;
}

export function appendToken(config: AxiosRequestConfig): AxiosRequestConfig {
  if (!config.headers) {
    config.headers = {};
  }
  config.headers.Authorization = `Bearer ${window.accessToken}`;

  return config;
}

function onRequest(request: AxiosRequestConfig, _requestAction: any, store: Store<StoreState>) {
  try {
    const state = store.getState();
    const timezoneOffset = getClientTimezoneOffset(state);

    if (typeof timezoneOffset === 'number') {
      if (!request.headers) {
        request.headers = {};
      }
      request.headers['X-Client-Timezone-Offset'] = timezoneOffset.toString();
    }

    request.baseURL = getBackendURL();
    return appendToken(request);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Error in onRequest interceptor:', err);
  }

  return request;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function onError(error: AxiosError<any>, requestAction: any, store: Store) {
  // Handle 403 for inspection-related endpoints specifically
  if (error?.response?.status === 403 && requestAction?.request?.url?.includes('/inspections/')) {
    // Redirect to inspection access denied page
    window.location.replace('/inspection-access-denied');
    throw error;
  }

  // Existing 403 handling for other endpoints
  if (error?.response?.data.code === 403 && error?.response?.data?.message === 'Forbidden User') {
    window.location.replace('/error');
  }
  if (requestAction?.meta?.errorMessage) {
    store.dispatch(app.actions.setSnackMessage({ message: requestAction.meta.errorMessage, type: 'error' }));
  }

  throw error;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function onSuccess(response: any, requestAction: any, store: Store) {
  if (requestAction?.meta?.successMessage) {
    store.dispatch(app.actions.setSnackMessage({ message: requestAction.meta.successMessage, type: 'success' }));
  }

  return response;
}

export { onRequest, onError, onSuccess };
